import { useEffect } from 'react';
import type { JsonObject } from '@backstage/types';
import type { IconElement } from '@backstage/frontend-plugin-api';
import {
  SearchResult as SearchResults,
  SearchPagination,
  useSearch,
  SearchResultPager,
} from '@backstage/plugin-search-react';
import { Container, Flex, Text } from '@backstage/ui';
import { useSearchPageContext } from './search-page-context';
import { ALL_TAB_PATH } from './search-tabs';
import { SearchInput } from './search-field';
import { FeaturedSearchResults } from './featured-search-results';

type SearchTabContentProps = {
  path: string;
  title: string;
  icon?: IconElement;
  defaultFilters?: JsonObject;
};

export const SearchTabContent = ({
  path,
  title,
  icon,
  defaultFilters,
}: SearchTabContentProps) => {
  const { setTypes, setFilters, result: searchResult } = useSearch();
  const {
    config,
    getResultItemComponent,
    resultTypes,
    searchFilters,
    filtersByType,
  } = useSearchPageContext();

  const tabFilters = filtersByType[path] ?? [];
  const numberOfResults = searchResult.value?.numberOfResults;
  const resultLabel = numberOfResults === 1 ? 'Result' : 'Results';
  let resultCountText = '';
  let resultPrefixText = 'Results for ';

  if (typeof numberOfResults === 'number') {
    resultCountText = `${numberOfResults} ${resultLabel}`;
    resultPrefixText = 'for ';
  }

  useEffect(() => {
    const types = path === ALL_TAB_PATH ? [] : [path];
    setTypes(types);
    setFilters(defaultFilters ?? {});
    // path and defaultFilters are stable constants per tab instance
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setTypes, setFilters]);

  return (
    <Container>
      <Flex align="center" px="4" pb="3">
        {icon}
        <Text
          variant="title-x-small"
          style={{ color: '#6B6560', whiteSpace: 'nowrap' }}
        >
          {resultCountText ? `${resultCountText} ` : ''}
          {resultPrefixText}
          <strong style={{ color: 'initial' }}>{title}</strong>
        </Text>
        {tabFilters.map(FilterComponent => (
          <FilterComponent key={FilterComponent.name} />
        ))}
        {searchFilters.map(SearchFilterComponent => (
          <SearchFilterComponent
            key={SearchFilterComponent.name}
            className=""
          />
        ))}
        <SearchInput />
      </Flex>

      <FeaturedSearchResults />

      <SearchPagination />

      <SearchResults>
        {({ results }) => (
          <>
            {results.map((result, idx) => {
              const { noTrack } = config;
              const { document, ...rest } = result;
              const ResultListItem = getResultItemComponent(result);
              const resultListIcon = resultTypes.find(
                rt => rt.value === result.type,
              )?.icon;

              return (
                <ResultListItem
                  {...rest}
                  key={`${result.type}-${idx}`}
                  result={document}
                  icon={resultListIcon}
                  noTrack={noTrack}
                />
              );
            })}
          </>
        )}
      </SearchResults>
      <SearchResultPager />
    </Container>
  );
};
